import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Gift,
  DollarSign,
  Copy,
  Check,
  Share2,
  Users,
  TrendingUp,
  Clock,
  Loader2,
  ExternalLink,
  Wallet,
  QrCode,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { QRCodeSVG } from 'qrcode.react';
import { PageBanner } from '@/components/PageBanner';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/PageSkeleton';
import { PageHeader } from '@/components/PageHeader';

export default function ReferralPage() {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const qrRef = useRef(null);
  
  // Data
  const [program, setProgram] = useState(null);
  const [myCode, setMyCode] = useState(null);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [programRes, referralsRes, creditsRes] = await Promise.all([
        api.get('/referrals/my-program'),
        api.get('/referrals/my-referrals'),
        api.get('/referrals/my-credits'),
      ]);
      
      setProgram(programRes.data.program);
      setMyCode(programRes.data.my_code);
      setStats(programRes.data.stats);
      setReferrals(referralsRes.data.referrals || []);
      setCredits(creditsRes.data);
    } catch (err) {
      console.error('Failed to load referral data:', err);
      // Don't show error for non-participants
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/referrals/generate-code', {
        expires_days: 90,
      });
      setMyCode(res.data.code);
      toast.success('Referral code generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate code');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (_err) {
      toast.error('Failed to copy');
    }
  };

  const getReferralLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/signup?ref=${myCode}`;
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent("Try Eva - You'll get a bonus!");
    const body = encodeURIComponent(
      `Hey!\n\nI've been using Eva for our team and thought you might find it valuable too.\n\n` +
      `Use my referral link to sign up and you'll get ${program?.referee_trial_extension_days || 7} extra trial days ` +
      `plus ${program?.referee_discount_percent || 20}% off your first month!\n\n` +
      `${getReferralLink()}\n\nLet me know if you have any questions!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareViaLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getReferralLink())}`, '_blank');
  };

  const downloadQRCode = () => {
    if (!qrRef.current) return;
    
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `eva-referral-${myCode}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    toast.success('QR code downloaded!');
  };

  const shareViaTwitter = () => {
    const text = encodeURIComponent(
      `Check out Eva - an AI-powered HR assistant that's saving our team hours! Use my referral link and get bonus trial days: ${getReferralLink()}`
    );
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  };

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="7xl"
        banner
        bannerPageKey="engagement-referral"
        kpis={4}
        sections={2}
        sectionClassName="h-72"
        data-testid="referral-loading"
      />
    );
  }

  // No program available
  if (!program) {
    return (
      <PageShell maxWidth="7xl" data-testid="referral-page">
          <PageHeader title="Refer & Earn" description="Invite colleagues and earn rewards" />

          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Gift className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">Coming Soon</h3>
              <p className="text-muted-foreground text-center max-w-md mt-2">
                Our referral program is being set up. Check back soon to start earning
                rewards for inviting your colleagues to Eva!
              </p>
            </CardContent>
          </Card>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="7xl" data-testid="referral-page">
      {/* Page Banner */}
      <PageBanner pageKey="engagement-referral" />
      
      <PageHeader title="Refer & Earn" description="Invite colleagues and earn cash rewards" />

      {/* Hero Card */}
      <Card className="bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent border-emerald-200 dark:border-emerald-800">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-2">
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                {program.type === 'trial' ? 'Trial Advocate Program' : 'Customer Referral'}
              </Badge>
              <h2 className="text-2xl font-bold">
                Earn up to ${(program.referrer_reward_on_signup || 0) + (program.referrer_reward_on_conversion || 0)} per referral
              </h2>
              <p className="text-muted-foreground max-w-md">
                Share Eva with your network. When they sign up and become customers,
                you both get rewarded with real cash rewards.
              </p>
            </div>
            
            <div className="flex flex-col items-center gap-3 p-4 rounded-lg bg-background/80 border min-w-[200px]">
              <Wallet className="h-8 w-8 text-emerald-500" />
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Your Balance</p>
                <p className="text-3xl font-bold text-emerald-600">
                  ${credits?.balance?.toFixed(2) || '0.00'}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Lifetime earned: ${credits?.lifetime_earned?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referral Link Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Your Referral Link
          </CardTitle>
          <CardDescription>
            Share this link with colleagues to earn rewards
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {myCode ? (
            <>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={getReferralLink()}
                  className="font-mono text-sm"
                />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(getReferralLink())}
                      >
                        {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Copy link</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Your code:</span>
                <Badge variant="outline" className="font-mono text-lg px-3 py-1">
                  {myCode}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(myCode)}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={shareViaEmail}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Share via Email
                </Button>
                <Button variant="outline" size="sm" onClick={shareViaLinkedIn}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Share on LinkedIn
                </Button>
                <Button variant="outline" size="sm" onClick={shareViaTwitter}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Share on X
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowQRModal(true)}>
                  <QrCode className="h-4 w-4 mr-2" />
                  Show QR Code
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-6">
              <p className="text-muted-foreground mb-4">
                Generate your unique referral code to start earning
              </p>
              <Button onClick={generateCode} disabled={generating} size="lg">
                {generating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Gift className="h-4 w-4 mr-2" />
                )}
                Generate My Referral Code
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card>
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center text-center p-4">
              <div className="h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center mb-3">
                <Share2 className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold mb-1">1. Share Your Link</h3>
              <p className="text-sm text-muted-foreground">
                Send your unique referral link to colleagues who could benefit from Eva
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center p-4">
              <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mb-3">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-semibold mb-1">2. They Sign Up</h3>
              <p className="text-sm text-muted-foreground">
                They get +{program.referee_trial_extension_days} trial days and {program.referee_discount_percent}% off their first month
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center p-4">
              <div className="h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center mb-3">
                <DollarSign className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="font-semibold mb-1">3. You Earn</h3>
              <p className="text-sm text-muted-foreground">
                ${program.referrer_reward_on_signup} when they sign up, ${program.referrer_reward_on_conversion} when they convert
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 rounded-lg bg-secondary/50 flex items-center justify-between">
            <div>
              <p className="font-medium">Maximum potential per referral</p>
              <p className="text-sm text-muted-foreground">
                {stats?.referrals_this_month || 0} of {program.max_referrals_per_month} referrals used this month
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-emerald-600">
                ${(program.referrer_reward_on_signup || 0) + (program.referrer_reward_on_conversion || 0)}
              </p>
              <p className="text-xs text-muted-foreground">per successful referral</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="h-4 w-4" />
              <span className="text-sm">This Month</span>
            </div>
            <p className="text-2xl font-bold mt-1">
              {stats?.referrals_this_month || 0}
            </p>
            <p className="text-xs text-muted-foreground">referrals</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              <span className="text-sm">Total Earned</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-emerald-600">
              ${stats?.total_earnings?.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs text-muted-foreground">lifetime</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4 text-amber-500" />
              <span className="text-sm">Pending</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-amber-600">
              ${stats?.pending_earnings?.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs text-muted-foreground">awaiting approval</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Wallet className="h-4 w-4 text-blue-500" />
              <span className="text-sm">Balance</span>
            </div>
            <p className="text-2xl font-bold mt-1">
              ${credits?.balance?.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs text-muted-foreground">available balance</p>
          </CardContent>
        </Card>
      </div>

      {/* Referral History */}
      <Card>
        <CardHeader>
          <CardTitle>Your Referrals</CardTitle>
          <CardDescription>
            Track the status of people you&apos;ve referred
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Referee</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Reward</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {referrals.map((referral) => (
                <TableRow key={referral.id}>
                  <TableCell>
                    <p className="font-medium">{referral.referee_email}</p>
                    <Badge variant="outline" className="text-xs mt-1">
                      {referral.program_name}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(referral.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge 
                      variant={referral.status === 'converted' ? 'default' : 'secondary'}
                      className={referral.status === 'converted' ? 'bg-emerald-500' : ''}
                    >
                      {referral.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className={`font-semibold ${
                        referral.referrer_reward_status === 'paid' ? 'text-emerald-600' : 'text-muted-foreground'
                      }`}>
                        ${referral.referrer_reward_amount?.toFixed(2) || '0.00'}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {referral.referrer_reward_status}
                      </Badge>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {referrals.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    <Gift className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No referrals yet</p>
                    <p className="text-sm">Share your link to start earning!</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* QR Code Modal */}
      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Your Referral QR Code
            </DialogTitle>
            <DialogDescription>
              Share this QR code to let others easily sign up with your referral
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col items-center gap-4 py-4">
            <div ref={qrRef} className="p-4 bg-white rounded-lg border shadow-sm">
              <QRCodeSVG 
                value={getReferralLink()} 
                size={200}
                level="H"
                includeMargin
              />
            </div>
            
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">Referral Code</p>
              <Badge variant="outline" className="font-mono text-lg px-4 py-1">
                {myCode}
              </Badge>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadQRCode}>
                <Download className="h-4 w-4 mr-2" />
                Download PNG
              </Button>
              <Button onClick={() => copyToClipboard(getReferralLink())}>
                <Copy className="h-4 w-4 mr-2" />
                Copy Link
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground text-center max-w-xs">
              When scanned, this QR code opens your referral signup link. 
              The referee will see their benefits before signing up.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
